import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { CustomDatePicker } from "../../components/custom-date-picker/custom-date-picker.component";
import {
	SammelrechnungTableRow,
	RecipientTableRow,
	JobDocumentTableRow,
	SammelrechnungTableActions,
} from "./sammelrechnungen-table.types";
import { SammelrechnungenCheckbox } from "./components/sammelrechnungen-checkbox.component";
import {
	getCheckboxState,
	getVisibleColumnsForRecipient,
	getCurrencyForJobs,
	sumAmountsByCurrency,
	applyAkontoByCurrency,
	AmountByCurrency,
} from "./sammelrechnungen-table.utils";
import { RecipientTypeEnum } from "./types";
import {
	JobDocumentTypeEnum,
	jobDocumentTypeToText,
} from "../job-page/job-document/job-document.types";
import { applyRounding } from "../../lib/utils/calculate";
import { RoundToType } from "../../store/Jobs/utils";
import {
	ClientEntityType,
	PatientEntityType,
	PractitionerEntityType,
} from "@/lib/supabase/supabaseTypes";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { LazyPatient } from "@/dentlab/src/store/Patient/patients.store";

// Format number with thousand separator
const formatCurrency = (value: number): string => {
	const isNegative = value < 0;
	const absoluteValue = Math.abs(value);
	const [integerPart, decimalPart] = absoluteValue.toFixed(2).split(".");
	const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
	return `${isNegative ? "-" : ""}${formattedInteger}.${decimalPart}`;
};

// Renders each currency's amount separately, e.g. "1'000.00 CHF + 500.00 EUR"
const formatAmountsByCurrency = (
	amounts: AmountByCurrency[],
	fallbackCurrency: string
): string => {
	if (amounts.length === 0) {
		return `${formatCurrency(0)} ${fallbackCurrency}`;
	}
	return amounts
		.map(
			({ currency, amount }) =>
				`${formatCurrency(
					applyRounding(amount, RoundToType.TO_SECOND_DECIMAL)
				)} ${currency}`
		)
		.join(" + ");
};

export const createSammelrechnungColumns = (
	actions: SammelrechnungTableActions,
	dateRange: SelectionRange,
	practitionersLookup: Record<number, PractitionerEntityType>,
	clientsLookup: Record<string, ClientEntityType>,
	patientsLookup: Record<string, PatientEntityType | LazyPatient>,
	patientName: (
		patient: PatientEntityType | LazyPatient,
		orderLastNameFirst?: boolean
	) => string,
	optimalWidths: Record<string, string>,
	invoiceDates: Record<string, Date>,
	globalInvoiceDate: Date | null,
	hasConflicts: boolean = false,
	tableData?: RecipientTableRow[],
	akontoLookup?: Record<string, number>,
	akontoEnabled?: Record<string, boolean>,
	onAkontoToggle?: (recipientKey: string) => void
): ColumnDef<SammelrechnungTableRow>[] => {
	const columns: ColumnDef<SammelrechnungTableRow>[] = [
		// Expander column
		{
			id: "expander",
			header: "",
			size: 40,
			cell: ({ row }) => {
				if (row.original.tableRowType === "jobDocument") return null;

				return row.getCanExpand() ? (
					<div className="flex justify-center items-center">
						<Button
							variant="ghost"
							size="sm"
							className="p-0 h-5 w-5"
							onClick={(e) => {
								e.stopPropagation();
								row.toggleExpanded();
							}}
						>
							{row.getIsExpanded() ? (
								<ChevronDown className="h-4 w-4" />
							) : (
								<ChevronRight className="h-4 w-4" />
							)}
						</Button>
					</div>
				) : (
					<div className="h-5 w-5" />
				);
			},
		},
		// Checkbox column
		{
			id: "selection",
			header: "",
			size: 50,
			cell: ({ row }) => {
				const rowData = row.original;

				if (rowData.tableRowType === "recipient") {
					const recipientRow = rowData as RecipientTableRow;
					const recipientKey = `${recipientRow.clientId}_${recipientRow.recipientId}`;
					const checkboxState = getCheckboxState(
						recipientRow,
						dateRange
					);

					return (
						<div
							className="flex justify-center items-center"
							data-testid={`sammelrechnung-recipient-checkbox-${recipientKey}`}
						>
							<SammelrechnungenCheckbox
								state={checkboxState}
								className="h-5 w-5"
								onChange={(checked) => {
									actions.onCycleRecipientSelection(
										recipientKey
									);
								}}
							/>
						</div>
					);
				} else {
					const jobDocRow = rowData as JobDocumentTableRow;
					// Find parent recipient to check if this job is selected
					const parentRecipient = row.getParentRow()
						?.original as RecipientTableRow;
					const isSelected =
						parentRecipient?.selectedJobDocumentIds.has(
							jobDocRow.id as number
						) || false;

					return (
						<div className="pl-4 flex justify-center items-center">
							<Checkbox
								checked={isSelected}
								className="h-5 w-5"
								onCheckedChange={() => {
									actions.onSelectJobDocument(
										jobDocRow.id as number,
										`${jobDocRow.clientId}_${jobDocRow.recipientId}`
									);
								}}
								onClick={(e) => {
									e.stopPropagation();
								}}
							/>
						</div>
					);
				}
			},
		},
		// Main content column
		{
			id: "content",
			header: "Empfänger / Details",
			size: 1000,
			cell: ({ row }) => {
				const rowData = row.original;

				if (rowData.tableRowType === "recipient") {
					const recipientRow = rowData as RecipientTableRow;
					const selectedJobs = recipientRow.subRows.filter((job) =>
						recipientRow.selectedJobDocumentIds.has(
							job.id as number
						)
					);
					const fallbackCurrency = getCurrencyForJobs(
						recipientRow.subRows
					);
					const totalAmounts = sumAmountsByCurrency(
						recipientRow.subRows
					);
					const selectedAmounts = sumAmountsByCurrency(selectedJobs);

					return (
						<div
							className="flex items-center gap-3"
							data-testid={`sammelrechnung-recipient-row-${recipientRow.clientId}_${recipientRow.recipientId}`}
						>
							<span className="font-medium text-sm">
								{recipientRow.recipientName}
							</span>
							<Badge
								variant={
									recipientRow.recipientType ===
									RecipientTypeEnum.GUARANTOR
										? "secondary"
										: "outline"
								}
								className="text-xs py-1 px-2 h-5"
							>
								{recipientRow.recipientType ===
								RecipientTypeEnum.GUARANTOR
									? "Garant"
									: "Kunde"}
							</Badge>
							<span className="text-sm text-gray-500">
								({recipientRow.jobDocumentCount} Offen)
							</span>
							<span className="text-sm text-gray-500">
								• Gesamt:{" "}
								{formatAmountsByCurrency(
									totalAmounts,
									fallbackCurrency
								)}
							</span>
							<span className="text-sm text-gray-500">
								• Ausgewählt:{" "}
								{formatAmountsByCurrency(
									selectedAmounts,
									fallbackCurrency
								)}
							</span>
						</div>
					);
				} else {
					const jobDocRow = rowData as JobDocumentTableRow;
					// Get the parent recipient to determine which columns to show
					const parentRecipient = row.getParentRow()
						?.original as RecipientTableRow;
					const visibleColumns = parentRecipient
						? getVisibleColumnsForRecipient(parentRecipient)
						: {
								date: true,
								patient: true,
								job: true,
								practitioner: false,
								client: false,
								type: true,
								monthlyDiscount: false,
								amount: true,
							};

					return (
						<div className="pl-4 flex items-center text-sm">
							{/* Date */}
							<div className={optimalWidths.date}>
								<span className="text-gray-600">
									{jobDocRow.date &&
										new Date(
											jobDocRow.date
										).toLocaleDateString("de-CH")}
								</span>
							</div>

							{/* Patient */}
							<div className={optimalWidths.patient}>
								<Button
									variant="ghost"
									size="sm"
									className="h-auto p-0 text-sm text-left justify-start font-normal text-gray-700 hover:text-gray-900 hover:bg-transparent"
									onClick={(e) => {
										e.stopPropagation();
										if (jobDocRow.job_patient_id) {
											actions.onOpenPatientDetail(
												jobDocRow.job_patient_id
											);
										}
									}}
								>
									{jobDocRow.job_patient_id &&
										patientName(
											patientsLookup[
												jobDocRow.job_patient_id
											],
											true
										)}
								</Button>
							</div>

							{/* Job */}
							<div className={optimalWidths.job}>
								<Button
									variant="ghost"
									size="sm"
									className="h-auto p-0 text-sm text-left justify-start font-normal text-gray-700 hover:text-gray-900 hover:bg-transparent"
									onClick={(e) => {
										e.stopPropagation();
										if (jobDocRow.job_id) {
											actions.onOpenJobDetail(
												jobDocRow.job_id
											);
										}
									}}
								>
									{jobDocRow.job_code} - {jobDocRow.job_title}
								</Button>
							</div>

							{/* Type */}
							<div className={optimalWidths.type}>
								<span className="text-gray-600">
									{jobDocRow.type
										? jobDocumentTypeToText[
												jobDocRow.type as JobDocumentTypeEnum
											] || `Debug: "${jobDocRow.type}"`
										: "-"}
								</span>
							</div>

							{/* Practitioner (conditional) */}
							{visibleColumns.practitioner &&
								optimalWidths.practitioner && (
									<div className={optimalWidths.practitioner}>
										{jobDocRow.job_practitioner_id &&
										practitionersLookup[
											jobDocRow.job_practitioner_id
										] ? (
											<span className="text-gray-600 text-sm">
												{getClientDisplayName(
													practitionersLookup[
														jobDocRow
															.job_practitioner_id
													] as unknown as ClientEntityType
												)}
											</span>
										) : (
											<span className="text-gray-400">
												-
											</span>
										)}
									</div>
								)}

							{/* Client (conditional - for guarantor) */}
							{visibleColumns.client && optimalWidths.client && (
								<div className={optimalWidths.client}>
									{jobDocRow.client_id &&
									clientsLookup[jobDocRow.client_id] ? (
										<span className="text-gray-600 text-sm">
											{getClientDisplayName(
												clientsLookup[
													jobDocRow.client_id
												]
											)}
										</span>
									) : (
										<span className="text-gray-400">-</span>
									)}
								</div>
							)}

							{/* Monthly Discount (conditional) */}
							{visibleColumns.monthlyDiscount &&
								optimalWidths.monthlyDiscount && (
									<div
										className={
											optimalWidths.monthlyDiscount
										}
									>
										<span className="text-gray-600 text-sm">
											{jobDocRow.is_ignore_monthly_discount
												? "Ja"
												: "Nein"}
										</span>
									</div>
								)}
						</div>
					);
				}
			},
		},
		// Amount column
		{
			id: "amount",
			header: "Betrag",
			size: hasConflicts ? 150 : 350,
			cell: ({ row }) => {
				const rowData = row.original;

				if (rowData.tableRowType === "recipient") {
					const recipientRow = rowData as RecipientTableRow;
					const recipientKey = `${recipientRow.clientId}_${recipientRow.recipientId}`;

					const akontoKey =
						recipientRow.recipientType ===
						RecipientTypeEnum.GUARANTOR
							? `guarantor:${recipientRow.recipientId}`
							: `client:${recipientRow.recipientId}`;
					const availableAkonto = akontoLookup?.[akontoKey] ?? 0;
					const isAkontoEnabled =
						akontoEnabled?.[recipientKey] ?? true;
					const selectedJobs = recipientRow.subRows.filter((job) =>
						recipientRow.selectedJobDocumentIds.has(
							job.id as number
						)
					);
					const fallbackCurrency = getCurrencyForJobs(
						selectedJobs.length > 0
							? selectedJobs
							: recipientRow.subRows
					);
					const netAmounts = applyAkontoByCurrency(
						sumAmountsByCurrency(selectedJobs),
						isAkontoEnabled ? availableAkonto : 0
					);

					return (
						<div className="text-left text-sm font-mono font-medium">
							{formatAmountsByCurrency(
								netAmounts,
								fallbackCurrency
							)}
						</div>
					);
				} else {
					const jobDocRow = rowData as JobDocumentTableRow;
					return (
						<div className="pl-4 text-left text-sm font-mono">
							{jobDocRow.amount !== undefined
								? formatCurrency(jobDocRow.amount || 0)
								: ""}{" "}
							{getCurrencyForJobs([jobDocRow])}
						</div>
					);
				}
			},
		},
		// Akonto available column
		{
			id: "akontoAvailable",
			header: "Akonto verfügbar",
			size: 150,
			cell: ({ row }) => {
				const rowData = row.original;
				if (rowData.tableRowType === "jobDocument") return null;

				const recipientRow = rowData as RecipientTableRow;
				const akontoKey =
					recipientRow.recipientType === RecipientTypeEnum.GUARANTOR
						? `guarantor:${recipientRow.recipientId}`
						: `client:${recipientRow.recipientId}`;
				const availableAkonto = akontoLookup?.[akontoKey] ?? 0;

				return (
					<div className="text-left text-sm font-mono">
						{availableAkonto > 0 ? (
							<span className="text-gray-700">
								{formatCurrency(
									applyRounding(
										availableAkonto,
										RoundToType.TO_SECOND_DECIMAL
									)
								)}{" "}
								{getCurrencyForJobs(recipientRow.subRows)}
							</span>
						) : (
							<span className="text-gray-400">-</span>
						)}
					</div>
				);
			},
		},
		// Akonto checkbox column
		{
			id: "akontoUse",
			header: "Akonto nutzen",
			size: 120,
			cell: ({ row }) => {
				const rowData = row.original;
				if (rowData.tableRowType === "jobDocument") return null;

				const recipientRow = rowData as RecipientTableRow;
				const recipientKey = `${recipientRow.clientId}_${recipientRow.recipientId}`;
				const akontoKey =
					recipientRow.recipientType === RecipientTypeEnum.GUARANTOR
						? `guarantor:${recipientRow.recipientId}`
						: `client:${recipientRow.recipientId}`;
				const availableAkonto = akontoLookup?.[akontoKey] ?? 0;
				const isEnabled = akontoEnabled?.[recipientKey] ?? true;

				return (
					<div className="flex justify-center items-center">
						<Checkbox
							checked={isEnabled}
							disabled={availableAkonto <= 0}
							onCheckedChange={() => {
								if (onAkontoToggle) {
									onAkontoToggle(recipientKey);
								}
							}}
							onClick={(e) => {
								e.stopPropagation();
							}}
							className="h-5 w-5"
						/>
					</div>
				);
			},
		},
		// Conditionally add conflicts column
		...(hasConflicts
			? [
					{
						id: "conflicts",
						header: "Konflikte",
						size: 200,
						cell: ({
							row,
						}: {
							row: Row<SammelrechnungTableRow>;
						}) => {
							const rowData = row.original;
							if (rowData.tableRowType === "jobDocument")
								return null;

							const recipientRow = rowData as RecipientTableRow;
							const recipientKey = `${recipientRow.clientId}_${recipientRow.recipientId}`;
							const conflicts =
								actions?.getSelectedJobsConflicts?.(
									recipientKey
								) || [];

							if (conflicts.length === 0) {
								return (
									<div className="text-left text-sm text-slate-400">
										-
									</div>
								);
							}

							return (
								<div className="text-left text-sm text-orange-600">
									{conflicts.join(", ")}
								</div>
							);
						},
					},
				]
			: []),
		// Invoice Date column
		{
			id: "invoiceDate",
			header: "Rechnungsdatum",
			size: 180,
			cell: ({ row }) => {
				const rowData = row.original;
				if (rowData.tableRowType === "jobDocument") return null;

				const recipientRow = rowData as RecipientTableRow;
				const recipientKey = `${recipientRow.clientId}_${recipientRow.recipientId}`;
				const currentDate =
					globalInvoiceDate ??
					(invoiceDates[recipientKey] || new Date());

				return (
					<div
						className="text-left"
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						<CustomDatePicker
							value={currentDate}
							onDateChange={(date) => {
								if (date) {
									actions.onInvoiceDateChange(
										recipientKey,
										date
									);
								}
							}}
							className="w-full"
							disabled={globalInvoiceDate !== null}
							title={
								globalInvoiceDate !== null
									? "Das globale Rechnungsdatum überschreibt diese Einstellung"
									: undefined
							}
						/>
					</div>
				);
			},
		},
		// Actions column
		{
			id: "actions",
			header: "Aktionen",
			size: 180,
			cell: ({ row }) => {
				const rowData = row.original;
				if (rowData.tableRowType === "jobDocument") return null;

				const recipientRow = rowData as RecipientTableRow;
				const recipientKey = `${recipientRow.clientId}_${recipientRow.recipientId}`;
				const conflicts =
					actions?.getSelectedJobsConflicts?.(recipientKey) || [];
				const hasConflicts = conflicts.length > 0;

				return (
					<div className="text-left">
						<Button
							variant="default"
							size="sm"
							className="h-8 px-3 text-sm"
							data-testid={`sammelrechnung-erstellen-${recipientKey}`}
							aria-label="Sammelrechnung erstellen"
							onClick={(e) => {
								e.stopPropagation();
								const selectedIds = Array.from(
									recipientRow.selectedJobDocumentIds
								);
								actions.onCreateInvoice(
									recipientKey,
									selectedIds
								);
							}}
							disabled={
								recipientRow.selectedJobDocumentIds.size ===
									0 || hasConflicts
							}
							title={
								hasConflicts
									? `Konflikte bei: ${conflicts.join(", ")}`
									: undefined
							}
						>
							Erstellen
						</Button>
					</div>
				);
			},
		},
	];

	// Check which conflicts exist in the data
	const hasAnyMixedVAT = tableData?.some((row) => row.hasMixedVAT) ?? false;
	const hasAnyMixedMwstNumber =
		tableData?.some((row) => row.hasMixedMwstNumber) ?? false;
	const hasAnyMixedMwstType =
		tableData?.some((row) => row.hasMixedMwstType) ?? false;
	const hasAnyMixedRounding =
		tableData?.some((row) => row.hasMixedRounding) ?? false;
	const hasAnyMixedCurrency =
		tableData?.some((row) => row.hasMixedCurrency) ?? false;

	// Find the position to insert conflict columns
	// They should go after the amount column AND after the conflicts summary column if it exists
	const amountColumnIndex = columns.findIndex((col) => col.id === "amount");
	const conflictsColumnIndex = columns.findIndex(
		(col) => col.id === "conflicts"
	);

	// Insert after conflicts column if it exists, otherwise after amount column
	let insertPosition =
		conflictsColumnIndex !== -1
			? conflictsColumnIndex
			: amountColumnIndex + 1;

	// Only add conflict columns that are actually needed
	if (amountColumnIndex !== -1 && hasAnyMixedVAT) {
		columns.splice(insertPosition++, 0, {
			id: "vat",
			header: "",
			size: 80,
			cell: ({ row }) => {
				const rowData = row.original;
				if (rowData.tableRowType === "recipient") return null;

				// Get parent recipient to check if this column should be shown
				const parentRow = row.getParentRow();
				const parentRecipient =
					parentRow?.original as RecipientTableRow;

				if (!parentRecipient?.hasMixedVAT) return null;

				const jobDocRow = rowData as JobDocumentTableRow;
				return (
					<div className="pl-4 text-left text-sm">
						{jobDocRow.job_tax_rate !== null
							? `${jobDocRow.job_tax_rate}%`
							: "-"}
					</div>
				);
			},
		});
	}

	if (amountColumnIndex !== -1 && hasAnyMixedMwstNumber) {
		columns.splice(insertPosition++, 0, {
			id: "mwstNumber",
			header: "",
			size: 120,
			cell: ({ row }) => {
				const rowData = row.original;
				if (rowData.tableRowType === "recipient") return null;

				// Get parent recipient to check if this column should be shown
				const parentRow = row.getParentRow();
				const parentRecipient =
					parentRow?.original as RecipientTableRow;

				if (!parentRecipient?.hasMixedMwstNumber) return null;

				const jobDocRow = rowData as JobDocumentTableRow;
				return (
					<div className="pl-4 text-left text-sm">
						{jobDocRow.job_mwst_number || "-"}
					</div>
				);
			},
		});
	}

	if (amountColumnIndex !== -1 && hasAnyMixedMwstType) {
		columns.splice(insertPosition++, 0, {
			id: "mwstType",
			header: "",
			size: 100,
			cell: ({ row }) => {
				const rowData = row.original;
				if (rowData.tableRowType === "recipient") return null;

				// Get parent recipient to check if this column should be shown
				const parentRow = row.getParentRow();
				const parentRecipient =
					parentRow?.original as RecipientTableRow;

				if (!parentRecipient?.hasMixedMwstType) return null;

				const jobDocRow = rowData as JobDocumentTableRow;
				const isEust = jobDocRow.job_is_mwst_eust;
				return (
					<div className="pl-4 text-left text-sm">
						{isEust === true
							? "EUSt"
							: isEust === false
								? "MwSt"
								: "-"}
					</div>
				);
			},
		});
	}

	if (amountColumnIndex !== -1 && hasAnyMixedRounding) {
		columns.splice(insertPosition++, 0, {
			id: "rounding",
			header: "",
			size: 100,
			cell: ({ row }) => {
				const rowData = row.original;
				if (rowData.tableRowType === "recipient") return null;

				// Get parent recipient to check if this column should be shown
				const parentRow = row.getParentRow();
				const parentRecipient =
					parentRow?.original as RecipientTableRow;

				if (!parentRecipient?.hasMixedRounding) return null;

				const jobDocRow = rowData as JobDocumentTableRow;
				const roundTo = jobDocRow.job_round_to;
				return (
					<div className="pl-4 text-left text-sm">
						{roundTo !== null && roundTo !== undefined
							? roundTo
							: "-"}
					</div>
				);
			},
		});
	}

	if (amountColumnIndex !== -1 && hasAnyMixedCurrency) {
		columns.splice(insertPosition++, 0, {
			id: "currency",
			header: "",
			size: 80,
			cell: ({ row }) => {
				const rowData = row.original;
				if (rowData.tableRowType === "recipient") return null;

				// Get parent recipient to check if this column should be shown
				const parentRow = row.getParentRow();
				const parentRecipient =
					parentRow?.original as RecipientTableRow;

				if (!parentRecipient?.hasMixedCurrency) return null;

				const jobDocRow = rowData as JobDocumentTableRow;
				return (
					<div className="pl-4 text-left text-sm">
						{getCurrencyForJobs([jobDocRow])}
					</div>
				);
			},
		});
	}

	return columns;
};
